// src/utils/transformProjectDetails.ts

interface ProjectFile {
  file_id: number;
  project_id: number;
  blob_file_name: string;
  file_name: string;
  document_classification: number;
  user_id: string;
  is_file_type_converted: number;
  is_vector_db_processed: number;
  status: boolean;
  meta_data: any;
  file_size: number;
  file_type: any;
  created_date: string | null;
}

interface ProjectDetailsResponse {
  code: number;
  message: string;
  data: Array<{
    project_id: number;
    project_code: string;
    RITM_number: string;
    project_name: string;
    project_brief: string;
    requested_by: string;
    project_lead_name: string;
    project_lead_email: string;
    request_manager: string;
    location: string;
    email: string;
    finance_lead_name: string;
    finance_lead_email: string;
    business_stack_req_group: string;
    cost_center: string;
    sector: string;
    geo_graphic_scope: string;
    is_new_consulting_project: string;
    consulting_type: string;
    estimated_project_start_date: string;
    estimated_project_end_date: string;
    is_consulting_work: string;
    preferred_vendor: string;
    core_deliverables: string;
    project_budget: string;
    is_follow_up: string;
    consulting_company: string;
    final_total_cost: string;
    purchase_order_number: string;
    opportunity_cost: string;
    is_address_business_problem: string;
    is_internal_team_address_business_problem: string;
    is_external_scope: string;
    is_proceed_further: string;
    form_type: number;
    rate_value_received: string;
    status: boolean;
    project_core_deliverables: string;
    close_out_additional_phase: string;
    created_date: string;
    updated_date: string;
    files: ProjectFile[];
  }>;
}

interface TransformedProject {
  RITM_number: string;
  project_id: number;
  is_address_business_problem: string;
  is_internal_team_address_business_problem: string;
  is_external_scope: string;
  is_proceed_further: string;
  requested_by: string;
  location: string;
  request_manager: string;
  email: string;
  project_lead_name: string;
  project_lead_email: string;
  business_stack_req_group: string;
  finance_lead_name: string;
  finance_lead_email: string;
  cost_center: string;
  sector: string;
  geo_graphic_scope: string;
  is_new_consulting_project: string;
  project_name: string;
  project_brief: string;
  consulting_type: string;
  estimated_project_start_date: string;
  estimated_project_end_date: string;
  is_consulting_work: string;
  preferred_vendor: string;
  core_deliverables: string;
  project_budget: string;
  is_follow_up: string;
  consulting_company: string;
  final_total_cost: string;
  project_core_deliverables: string;
  rate_value_received: string;
  purchase_order_number: string;
  close_out_additional_phase: string;
}

export const transformProjectDetails = (
  response: ProjectDetailsResponse
): {
  projectData: TransformedProject;
  files: ProjectFile[];
} => {
  const project = response.data[0]; // Assuming there's only one project in the array
  const transformedProjectData: TransformedProject = {
    RITM_number: project.RITM_number,
    project_id: project.project_id,
    is_address_business_problem: project.is_address_business_problem,
    is_internal_team_address_business_problem:
      project.is_internal_team_address_business_problem,
    is_external_scope: project.is_external_scope,
    is_proceed_further: project.is_proceed_further,
    requested_by: project.requested_by,
    location: project.location,
    request_manager: project.request_manager,
    email: project.email,
    project_lead_name: project.project_lead_name,
    project_lead_email: project.project_lead_email,
    business_stack_req_group: project.business_stack_req_group,
    finance_lead_name: project.finance_lead_name,
    finance_lead_email: project.finance_lead_email,
    cost_center: project.cost_center,
    sector: project.sector,
    geo_graphic_scope: project.geo_graphic_scope,
    is_new_consulting_project: project.is_new_consulting_project,
    project_name: project.project_name,
    project_brief: project.project_brief,
    consulting_type: project.consulting_type,
    estimated_project_start_date: project.estimated_project_start_date
      ? new Date(project.estimated_project_start_date).toLocaleDateString(
          "en-GB"
        )
      : project.estimated_project_start_date,
    estimated_project_end_date: project.estimated_project_end_date
      ? new Date(project.estimated_project_end_date).toLocaleDateString("en-GB")
      : project.estimated_project_end_date,
    is_consulting_work: project.is_consulting_work,
    preferred_vendor: project.preferred_vendor,
    core_deliverables: project.core_deliverables,
    project_budget: project.project_budget,
    is_follow_up: project.is_follow_up,
    consulting_company: project.consulting_company,
    final_total_cost: project.final_total_cost
      ? project.final_total_cost.replace(/^\$/, "")
      : project.final_total_cost,
    project_core_deliverables: project.project_core_deliverables,
    rate_value_received: project.rate_value_received,
    purchase_order_number: project.purchase_order_number,
    close_out_additional_phase: project.close_out_additional_phase,
  };
  return {
    projectData: transformedProjectData,
    files: project.files,
  };
};
